import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, ShoppingCart, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCurrency } from "../lib/currency";
import { toast } from "sonner";
import SEOHead from "../components/SEOHead";

const CATEGORIES = [
  { id: "website",    label: "Website Design",   sub: "One-time builds" },
  { id: "seo",        label: "SEO",              sub: "Monthly retainers" },
  { id: "api",        label: "API Integration",  sub: "One-time delivery" },
  { id: "automation", label: "Automation",       sub: "Custom workflows" },
  { id: "hosting",    label: "Hosting",          sub: "Monthly cloud plans" },
  { id: "ecommerce",  label: "Ecommerce",        sub: "Storefront builds" },
];

const PERIOD = {
  yearly: "/mo",  // legacy fallback — all recurring is now monthly
  monthly: "/mo",
  one_time: " one-time",
};

export default function Pricing() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("website");
  const { addToCart, user } = useAuth();
  const { format, current, code: currentCode } = useCurrency();
  // Anonymous visitors always see the promo (they're first-timers by default).
  // Signed-in customers see it only if the backend confirms they still qualify.
  const [promoActive, setPromoActive] = useState(!user);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/products`, { params: { category } });
        // Ensure consistent order by price asc
        setProducts([...data].sort((a, b) => a.price - b.price));
      } catch { toast.error("Failed to load plans"); }
      finally { setLoading(false); }
    };
    fetch();
  }, [category]);

  // Check server-side eligibility whenever the user changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) { setPromoActive(true); return; }
      try {
        const { data } = await api.get("/orders/eligibility");
        if (!cancelled) setPromoActive(Boolean(data.new_customer));
      } catch {
        if (!cancelled) setPromoActive(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const add = (p) => {
    addToCart({ sku: p.sku, name: p.name, price: p.price });
    toast.success(`${p.name} added to cart`);
  };

  const activeCat = useMemo(() => CATEGORIES.find(c => c.id === category) || CATEGORIES[0], [category]);

  // Country/currency-aware Schema.org markup: emits a Product+Offer for every visible plan
  // so Google Merchant + rich results render the right currency to each visitor.
  const productSchema = useMemo(() => {
    if (!products.length) return null;
    return {
      "@context": "https://schema.org",
      "@graph": products.map((p) => ({
        "@type": "Product",
        "name": p.name,
        "description": p.description,
        "category": activeCat.label,
        "brand": {"@type": "Brand", "name": "Optivize Digital"},
        "offers": {
          "@type": "Offer",
          "priceCurrency": currentCode,
          "price": Number(((promoActive ? p.price * 0.9 : p.price) * current.rate).toFixed(2)),
          "availability": "https://schema.org/InStock",
          "url": typeof window !== "undefined" ? `${window.location.origin}/pricing` : "https://optivize.digital/pricing",
          "seller": {"@type": "Organization", "name": "Optivize Digital"},
        },
      })),
    };
  }, [products, currentCode, current.rate, activeCat.label, promoActive]);

  return (
    <div>
      <SEOHead
        title={`${activeCat.label} Pricing — Optivize Digital`}
        description={`${activeCat.label} plans starting at low prices, billed in ${currentCode}. 5 tiers per service. Transparent pricing across 8 currencies.`}
        schema={productSchema}
      />
      {/* Hero */}
      <section className="hero-blue py-20 px-5">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl sm:text-6xl font-extrabold text-white" style={{fontFamily:'Outfit'}}>
            Simple, <span style={{color:'var(--accent-yellow)'}}>transparent</span> pricing
          </h1>
          <p className="text-blue-100 mt-4 text-lg">6 services. 5 plans each. Prices shown in <span className="font-semibold text-white">{currentCode}</span>.</p>
        </div>
      </section>

      {/* Category tabs */}
      <section className="py-12 px-5">
        <div className="max-w-6xl mx-auto">
          {promoActive && (
            <div
              className="mb-8 rounded-2xl border border-emerald-400/30 bg-gradient-to-r from-emerald-500/10 via-cyan-500/10 to-emerald-500/10 px-5 py-4 flex items-center gap-3 shadow-[0_10px_40px_-20px_rgba(16,185,129,0.5)]"
              data-testid="pricing-promo-banner"
            >
              <div className="shrink-0 h-10 w-10 rounded-full bg-emerald-500/15 flex items-center justify-center">
                <Sparkles size={18} className="text-emerald-300"/>
              </div>
              <div className="flex-1">
                <div className="text-white font-semibold" style={{fontFamily:'Outfit'}}>
                  {user ? "You still qualify" : "New here?"} — 10% off, auto-applied at checkout
                </div>
                <div className="text-slate-300 text-xs mt-0.5">
                  Optivize10 activates automatically on your first order. No code, no coupon hunt. Only a flat 2% Payment Gateway Fee — no hidden GST.
                </div>
              </div>
              <span className="hidden sm:inline-flex items-center px-3 py-1.5 rounded-full bg-emerald-500 text-white text-xs font-bold tracking-wide">
                OPTIVIZE10
              </span>
            </div>
          )}
          <div className="flex flex-wrap justify-center gap-2 mb-3" data-testid="pricing-categories">
            {CATEGORIES.map(c => (
              <button
                key={c.id}
                onClick={()=>setCategory(c.id)}
                data-testid={`cat-${c.id}`}
                className={`px-4 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  category === c.id
                    ? "bg-blue-600 text-white shadow-[0_8px_20px_-8px_rgba(37,99,235,0.7)]"
                    : "bg-slate-800/60 text-slate-300 border border-slate-700 hover:border-blue-500 hover:text-white"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <p className="text-center text-slate-400 text-sm">{activeCat.sub}</p>
        </div>
      </section>

      {/* Plans grid - 5 plans per category */}
      <section className="pb-20 px-5">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-400" size={28}/></div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5" data-testid="pricing-grid">
              {products.map((p, i) => (
                <motion.div
                  key={p.sku}
                  initial={{opacity:0, y:20}} animate={{opacity:1, y:0}}
                  transition={{duration:.4, delay:i*0.04}}
                  className={`card p-6 flex flex-col relative ${p.popular ? "ring-2 ring-blue-500 shadow-[0_12px_40px_-12px_rgba(37,99,235,0.5)]" : ""}`}
                  data-testid={`plan-${p.sku}`}
                >
                  {p.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded-full bg-blue-600 text-white">
                      Most Popular
                    </span>
                  )}
                  <h3 className="text-xl font-bold text-white" style={{fontFamily:'Outfit'}}>{p.name}</h3>
                  <p className="text-slate-400 text-sm mt-1 min-h-[40px]">{p.description}</p>
                  <div className="mt-4" itemScope itemType="https://schema.org/Offer">
                    <meta itemProp="priceCurrency" content={currentCode} />
                    <meta itemProp="price" content={String(((promoActive ? p.price * 0.9 : p.price) * current.rate).toFixed(2))} />
                    {promoActive ? (
                      <>
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-4xl font-extrabold text-white" style={{fontFamily:'Outfit'}} data-testid={`price-final-${p.sku}`}>
                            {format(Math.round(p.price * 0.9))}
                          </span>
                          <span className="text-slate-500 text-lg line-through" data-testid={`price-original-${p.sku}`}>
                            {format(p.price)}
                          </span>
                          <span className="text-slate-400 text-sm">{PERIOD[p.billing_cycle] || ""}</span>
                        </div>
                        <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5 rounded-full" data-testid={`promo-badge-${p.sku}`}>
                          <Sparkles size={10}/> Optivize10 auto-applied · saves {format(Math.round(p.price * 0.1))}
                        </div>
                      </>
                    ) : (
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-4xl font-extrabold text-white" style={{fontFamily:'Outfit'}}>{format(p.price)}</span>
                        <span className="text-slate-400 text-sm">{PERIOD[p.billing_cycle] || ""}</span>
                      </div>
                    )}
                  </div>
                  <ul className="mt-5 space-y-2 text-sm text-slate-300 flex-1">
                    {(p.features || []).map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <Check size={14} className="text-blue-400 mt-0.5 shrink-0" /> <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => add(p)}
                    className={`mt-6 w-full justify-center ${p.popular ? "btn-primary" : "btn-ghost"}`}
                    data-testid={`add-${p.sku}`}
                  >
                    <ShoppingCart size={14}/> Add to cart
                  </button>
                </motion.div>
              ))}
            </div>
          )}

          <div className="mt-12 text-center">
            <Link to="/checkout" className="btn-primary" data-testid="goto-checkout-btn">Review cart →</Link>
            <p className="text-xs text-slate-500 mt-3">
              Payment is captured securely at checkout. Our team reviews every order and moves it into Processing within 1 business day.
              {promoActive && " New-customer 10% off is applied automatically — no code required."}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
